import clsx from 'clsx'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { AccessiblePrice } from '@/components/AccessiblePrice'
import { useSelectedTypeOfContractAtom } from '@/components/ProductData/ProductDataProvider'
import type { ProductOfferFragment } from '@/services/graphql/generated'
import { useFormatter } from '@/utils/useFormatter'
import { Card } from 'ui/src/components/Card/Card'
import { ConditionalWrapper } from 'ui/src/components/ConditionalWrapper'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { ResizablePanel } from 'ui/src/components/ResizeablePanel'
import { Text } from 'ui/src/components/Text/Text'
import { CheckIcon } from 'ui/src/icons/CheckIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { CardRadioGroup } from '../CardRadioGroup/CardRadioGroup'
import { NonCoverageInfoDialogContent } from '../NonCoverageInfoDialogContent'
import { TierComparisonDialog } from '../TierComparisonDialog'
import { TierCoverageInfoDialogContent } from './TierCoverageInfoDialogContent/TierCoverageInfoDialogContent'
import { textTransition, vars } from '../CardRadioGroup/CardRadioGroup.css'

type Props = {
  offers: Array<ProductOfferFragment>
  selectedOffer: ProductOfferFragment
  onValueChange: (offerId: string) => void
}

export function ProductTierSelector({ offers, selectedOffer, onValueChange }: Props) {
  const formatter = useFormatter()
  const setTypeOfContract = useSetAtom(useSelectedTypeOfContractAtom())
  const { t } = useTranslation('purchase-form')

  const onOpenCoverageDialog = () => {
    setTypeOfContract(selectedOffer.variant.typeOfContract)
  }

  return (
    <>
      <CardRadioGroup.Root value={selectedOffer.id} onValueChange={onValueChange}>
        {offers.map((offer) => {
          const isSelected = selectedOffer.id === offer.id
          const hasPriceMatch = gotPriceMatched(offer)
          const monthlyCost = offer.cost.baseInsuranceGross

          return (
            <LargeDialog.Root key={offer.variant.typeOfContract}>
              <CardRadioGroup.Item
                className={yStack({ gap: 'md' })}
                value={offer.id}
                aria-labelledby={`${offer.id}-tier-title`}
                aria-describedby={`${offer.id}-tier-price ${offer.id}-tier-content`}
              >
                {hasPriceMatch && (
                  <CardRadioGroup.Ribbon>
                    <Text as="p" size="sm" align="center" singleLine>
                      {isSelected &&
                        t('TIER_RIBBON_CURRENT_PRICE', {
                          currentPrice: formatter.monthlyPrice(offer.priceMatch.externalPrice),
                        })}
                      {!isSelected && t('TIER_RIBBON', { company: offer.priceMatch.externalInsurer.displayName })}
                    </Text>
                  </CardRadioGroup.Ribbon>
                )}
                <Card.Aside floating>
                  <CardRadioGroup.Badge isSelected={isSelected}>
                    <span id={`${offer.id}-tier-price`}>
                      <AccessiblePrice price={monthlyCost} />
                    </span>
                  </CardRadioGroup.Badge>
                </Card.Aside>
                <Card.Header>
                  <Card.Title
                    id={`${offer.id}-tier-title`}
                    as="h2"
                    className={xStack({ alignItems: 'center', columnGap: 'xxs', flexWrap: 'wrap' })}
                  >
                    {offer.product.categoryDisplayNameShort ? (
                      <>
                        <Text as="span" style={{ color: vars.titleColor }} className={textTransition} size="xl">
                          {offer.product.categoryDisplayNameShort}
                        </Text>
                        <Text as="span" color="textSecondary" size="xl">
                          {offer.variant.displayNameSubtype}
                        </Text>
                      </>
                    ) : (
                      <Text as="span" style={{ color: vars.titleColor }} className={textTransition} size="xl">
                        {offer.variant.displayNameSubtype}
                      </Text>
                    )}
                  </Card.Title>
                </Card.Header>
                <ResizablePanel>
                  <Card.Content id={`${offer.id}-tier-content`} className={yStack({ gap: 'md' })}>
                    {offer.variant.tierDescription && (
                      <Text className={textTransition} style={{ color: vars.descriptionColor }}>
                        {offer.variant.tierDescription}
                      </Text>
                    )}
                    {isSelected && offer.usps.length > 0 && (
                      <ul className={yStack({ gap: 'xs' })}>
                        {offer.usps.map((usp) => (
                          <li key={usp} className={xStack({ gap: 'xs', alignItems: 'flex-start' })}>
                            <CheckIcon
                              size="1rem"
                              color={vars.descriptionColor}
                              // margin top is applied to visually align the icon with the text
                              className={clsx(sprinkles({ marginTop: 'xxs' }), textTransition)}
                            />
                            <Text className={textTransition}>{usp}</Text>
                          </li>
                        ))}
                      </ul>
                    )}

                    <ConditionalWrapper
                      condition={isSelected}
                      wrapWith={(children) => (
                        <Dialog.Trigger onClick={onOpenCoverageDialog} asChild>
                          {children}
                        </Dialog.Trigger>
                      )}
                    >
                      <CardRadioGroup.Button
                        className={sprinkles({ mt: 'xs' })}
                        variant="secondary"
                        size="medium"
                        fullWidth
                      >
                        {isSelected
                          ? t('TIER_COVERAGE_BUTTON')
                          : t('TIER_SELECT_BUTTON', { tierName: offer.variant.displayNameSubtype })}
                      </CardRadioGroup.Button>
                    </ConditionalWrapper>
                  </Card.Content>
                </ResizablePanel>
              </CardRadioGroup.Item>

              {offer.product.multiple && (
                <TierCoverageInfoDialogContent
                  title={offer.variant.displayNameSubtype || offer.variant.displayName}
                  subtitle={t('TIER_COVERAGE_DIALOG_SUBTITLE')}
                  selectedOffer={offer}
                />
              )}

              {offers.length === 1 && !offer.product.multiple && <NonCoverageInfoDialogContent />}
            </LargeDialog.Root>
          )
        })}
      </CardRadioGroup.Root>

      {offers.length > 1 && (
        <TierComparisonDialog
          title={t('COMPARE_TIERS_DIALOG_TITLE')}
          subtitle={t('COMPARE_TIERS_DIALOG_SUBTITLE')}
          showCoverageText={true}
        />
      )}
    </>
  )
}

function gotPriceMatched(
  offer: ProductOfferFragment,
): offer is ProductOfferFragment & { priceMatch: NonNullable<ProductOfferFragment['priceMatch']> } {
  return Boolean(offer.priceMatch && offer.priceMatch.priceReduction.amount > 0)
}
